import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withOrchestratorClient,
  getBaseUrlOverride,
} from './portal-http';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { ApplicationsContract } from '@terra/shared-contracts-applications';
import {
  type ApplicationNoteCreate,
  type ApplicationNoteUpdate,
  type ApplicationGuardrailCreate,
  type ApplicationGuardrailUpdate,
  type MergeAssetsPublicBodyDto,
  type UpdateApplicationRequestDto,
} from '@terra/shared-contracts-applications';
import type { DomainSourceType } from '@terra/shared-schemas-orchestrator';
import axios from 'axios';

export const portalApplicationsApi = {
  getApplicationById: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getApplication>({
      endpointFn: (headers) =>
        applicationsClient.getApplication({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getApplicationRepositories: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getRepositories>({
      endpointFn: (headers) =>
        applicationsClient.getRepositories({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  addDomain: (
    applicationId: string,
    domain: string,
    source: DomainSourceType,
  ) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.addDomain>({
      endpointFn: (headers) =>
        applicationsClient.addDomain({
          params: { id: applicationId },
          body: {
            domain,
            source,
          },
          ...withOrchestratorClient(headers),
        }),
    }),

  updateApplication: async (
    data: UpdateApplicationRequestDto & { id: string },
  ) => {
    try {
      const headers = await createApiHeaders();
      const response = await applicationsClient.updateApplication({
        params: { id: data.id },
        body: {
          crawlerPeriodic: data.crawlerPeriodic,
          smokeTestEnabled: data.smokeTestEnabled,
          automationSchedule: data.automationSchedule,
          supportsParallelExecution: data.supportsParallelExecution,
          domains: data.domains,
          isInternalApp: data.isInternalApp,
          signalCategoriesInScope: data.signalCategoriesInScope,
          discoveryContentTypes: data.discoveryContentTypes,
          dynamicApplicationScanEnabled: data.dynamicApplicationScanEnabled,
          dynamicApplicationScanScheduleRule:
            data.dynamicApplicationScanScheduleRule,
          dynamicApplicationScanCredentialId:
            data.dynamicApplicationScanCredentialId,
          dynamicApplicationScanVulnerabilityEnabled:
            data.dynamicApplicationScanVulnerabilityEnabled,
          dynamicApplicationScanDirectoryEnabled:
            data.dynamicApplicationScanDirectoryEnabled,
          targetRequestsPerMinute: data.targetRequestsPerMinute,
        },
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to update application');
    } catch (error) {
      console.error('Failed to update application:', error);

      throw error;
    }
  },

  // Application Files upload

  updateApplicationSmokeTest: async (
    applicationId: string,
    smokeTestEnabled: boolean,
  ) => {
    try {
      const headers = await createApiHeaders();
      const response = await applicationsClient.updateApplication({
        params: { id: applicationId },
        body: { smokeTestEnabled },
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to update smoke test setting');
    } catch (error) {
      console.error('Failed to update smoke test setting:', error);

      throw error;
    }
  },

  uploadApplicationFiles: async ({
    applicationId,
    files,
    fileType,
    onUploadProgress,
  }: {
    applicationId: string;
    files: File[];
    fileType: 'DOCS' | 'SWAGGER' | 'TESTING_REPORTS';
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total?: number;
    }) => void;
  }) => {
    try {
      const headers = await createApiHeaders();
      const formData = new FormData();

      files.forEach((file) => formData.append('files', file));
      formData.append('fileType', fileType);

      const baseUrl = getBaseUrlOverride('orchestrator');

      const response = await axios.put(
        `${baseUrl}/applications/${applicationId}/files`,
        formData,
        {
          withCredentials: true,
          headers: {
            ...headers,
          },
          onUploadProgress: onUploadProgress
            ? (progressEvent) => {
                onUploadProgress({
                  loaded: progressEvent.loaded,
                  total: progressEvent.total,
                });
              }
            : undefined,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload application files:', error);

      throw error;
    }
  },

  // Application Notes endpoints

  getApplicationScoping: async (applicationId: string) => {
    try {
      const headers = await createApiHeaders();
      const response = await applicationsClient.getApplicationScoping({
        params: { id: applicationId },
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return response.body;
      }

      return null;
    } catch (error) {
      console.error('Failed to get application scoping:', error);

      return null;
    }
  },

  uploadApplicationScoping: async (applicationId: string, files: File[]) => {
    try {
      const headers = await createApiHeaders();

      const response = await applicationsClient.scopeApplicationByFiles({
        params: { id: applicationId },
        body: { files },
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to upload scoping files');
    } catch (error) {
      console.error('Failed to upload application scoping:', error);

      throw error;
    }
  },

  triggerRepositoryScoping: async (applicationId: string) => {
    try {
      const headers = await createApiHeaders();
      const response = await applicationsClient.scopeRepositoriesFromCode({
        params: { id: applicationId },
        ...withOrchestratorClient(headers),
      });

      if (response.status === 200) {
        return response.body;
      }

      throw new Error('Failed to trigger repository scoping');
    } catch (error) {
      console.error('Failed to trigger repository scoping:', error);

      throw error;
    }
  },

  // Smoke test configuration

  getApplicationNotes: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getApplicationNotes>({
      endpointFn: (headers) =>
        applicationsClient.getApplicationNotes({
          params: { id: applicationId },
          ...withOrchestratorClient(headers),
        }),
    }),

  createApplicationNote: (applicationId: string, body: ApplicationNoteCreate) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.createApplicationNote>({
      endpointFn: (headers) =>
        applicationsClient.createApplicationNote({
          params: { id: applicationId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  updateApplicationNote: (
    applicationId: string,
    noteId: string,
    body: ApplicationNoteUpdate,
  ) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.updateApplicationNote>({
      endpointFn: (headers) =>
        applicationsClient.updateApplicationNote({
          params: { id: applicationId, noteId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteApplicationNote: (applicationId: string, noteId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.deleteApplicationNote>({
      endpointFn: (headers) =>
        applicationsClient.deleteApplicationNote({
          params: { id: applicationId, noteId },
          ...withOrchestratorClient(headers),
        }),
    }),

  // Comments endpoints

  getApplicationGuardrails: (applicationId: string) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.getApplicationGuardrails>(
      {
        endpointFn: (headers) =>
          applicationsClient.getApplicationGuardrails({
            params: { id: applicationId },
            ...withOrchestratorClient(headers),
          }),
      },
    ),

  createApplicationGuardrail: (
    applicationId: string,
    body: ApplicationGuardrailCreate,
  ) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.createApplicationGuardrail
    >({
      endpointFn: (headers) =>
        applicationsClient.createApplicationGuardrail({
          params: { id: applicationId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  updateApplicationGuardrail: (
    applicationId: string,
    guardrailId: string,
    body: ApplicationGuardrailUpdate,
  ) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.updateApplicationGuardrail
    >({
      endpointFn: (headers) =>
        applicationsClient.updateApplicationGuardrail({
          params: { id: applicationId, guardrailId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteApplicationGuardrail: (applicationId: string, guardrailId: string) =>
    tenantAwareApiWrapper<
      typeof ApplicationsContract.deleteApplicationGuardrail
    >({
      endpointFn: (headers) =>
        applicationsClient.deleteApplicationGuardrail({
          params: { id: applicationId, guardrailId },
          ...withOrchestratorClient(headers),
        }),
    }),

  mergeAssets: ({
    applicationId,
    body,
  }: {
    applicationId: string;
    body: MergeAssetsPublicBodyDto;
  }) =>
    tenantAwareApiWrapper<typeof ApplicationsContract.mergeAssets>({
      endpointFn: (headers) =>
        applicationsClient.mergeAssets({
          params: { id: applicationId },
          body,
          ...withOrchestratorClient(headers),
        }),
    }),
};
